import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Upload, Sparkles, Download, Loader2, Plus, ArrowLeft, Lock, Trash2, Pencil, Check, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckoutConsents } from "@/components/CheckoutConsents";
import AdminEditableImage from "@/components/AdminEditableImage";
import { LEGAL_VERSION } from "@/config/company";
import { toast } from "sonner";

const emptyExp = { role: "", company: "", period: "", description: "" };
const emptyEdu = { degree: "", institution: "", period: "" };

const CvSmartPremium = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [language, setLanguage] = useState(lang === "en" ? "en" : "it");
  const [template, setTemplate] = useState("moderno");
  const [templates, setTemplates] = useState([]);
  const [step, setStep] = useState("gallery"); // gallery | form
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [access, setAccess] = useState(null); // null=loading, {unlocked, price}
  const [unlocking, setUnlocking] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const isAdmin = user?.role === "admin";
  const [previewCustomer, setPreviewCustomer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallId, setPaywallId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [pwTerms, setPwTerms] = useState(false);
  const [pwImmediate, setPwImmediate] = useState(false);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cv/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.storage_path);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setPhotoUploading(false); }
  };

  // scratch form
  const [form, setForm] = useState({ name: "", title: "", email: "", phone: "", location: "", summary: "", skills: "", languages: "", certifications: "", experience: [ { ...emptyExp } ], education: [ { ...emptyEdu } ] });
  const [scratchJob, setScratchJob] = useState("");
  // tailor
  const [cvText, setCvText] = useState("");
  const [jobText, setJobText] = useState("");
  const [parsing, setParsing] = useState(false);

  const loadHistory = () => api.get("/cv/mine").then(({ data }) => setHistory(data)).catch(() => {});
  const checkAccess = () => api.get("/cv/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ unlocked: true, price: 2.99, symbol: "€" }));
  const loadTemplates = () => api.get(`/cv/templates?lang=${lang}`).then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { loadHistory(); checkAccess(); }, []);
  useEffect(() => { loadTemplates(); }, [lang]); // eslint-disable-line
  useEffect(() => { const eid = params.get("edit"); if (eid) loadForEdit(eid); }, []); // eslint-disable-line

  useEffect(() => {
    const sid = params.get("session_id");
    const paidCv = params.get("paid_cv");
    if (params.get("cancelled")) { toast.info(t("cv.payCancelled")); setParams({}); return; }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success(t("cv.paidSuccess"));
          const cid = data.cv_id || paidCv;
          if (cid) { await loadForEdit(cid); await loadPreview(cid); }
          loadHistory(); setParams({}); return;
        }
      } catch {}
      if (tries < 10) setTimeout(poll, 2000); else setParams({});
    };
    poll();
  }, []); // eslint-disable-line

  // reload the preview (watermark on/off) when admin toggles "preview as customer"
  useEffect(() => {
    if (result?.id) loadPreview(result.id);
  }, [previewCustomer]); // eslint-disable-line

  const loadPreview = async (id) => {
    try {
      const res = await api.get(`/cv/${id}/preview`, { params: { preview: (isAdmin && previewCustomer) ? 1 : 0 }, responseType: "blob" });
      setPreviewUrl((u) => { if (u) URL.revokeObjectURL(u); return URL.createObjectURL(res.data); });
    } catch { setPreviewUrl(null); }
  };
  // effective "downloadable" state: admins get free access unless previewing as a customer
  const canDl = (paid) => (isAdmin && !previewCustomer ? true : !!paid);
  const openPaywall = (id) => { setPaywallId(id); setPwTerms(false); setPwImmediate(false); setPaywallOpen(true); };
  const startCheckout = async () => {
    if (!pwTerms || !pwImmediate) { toast.error(t("consent.required")); return; }
    setCheckingOut(true);
    try {
      const { data } = await api.post(`/cv/${paywallId}/checkout`, { origin_url: window.location.origin, preview: isAdmin && previewCustomer, terms_accepted: pwTerms, immediate_supply_accepted: pwImmediate, legal_version: LEGAL_VERSION, purchase_type: "smart_service" });
      if (data.already_paid) {
        toast.success(t("cv.paidSuccess"));
        setResult((r) => (r ? { ...r, paid: true } : r));
        await loadPreview(paywallId); loadHistory(); setPaywallOpen(false); setCheckingOut(false); return;
      }
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); setCheckingOut(false); }
  };

  const doRename = async (id) => {
    try { await api.patch(`/cv/${id}`, { title: renameVal }); setRenameId(null); loadHistory(); toast.success(t("admin.saved")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doDelete = async (id) => {
    if (!window.confirm(t("cv.confirmDelete"))) return;
    try { await api.delete(`/cv/${id}`); loadHistory(); toast.success(t("cv.deleted")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cv/parse-upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setCvText(data.text || "");
      toast.success(t("cv.parsed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setParsing(false); }
  };

  const generate = async (mode) => {
    setLoading(true); setResult(null);
    try {
      const payload = mode === "scratch"
        ? { mode: "scratch", language, template, photo_path: photoPath, job_text: scratchJob, form: {
            ...form,
            skills: form.skills, languages: form.languages,
            experience: form.experience, education: form.education,
          } }
        : { mode, language, template, photo_path: photoPath, source_cv_text: cvText, job_text: jobText };
      const { data } = await api.post("/cv/generate", payload, { timeout: 120000 });
      setResult(data);
      loadPreview(data.id);
      loadHistory();
      toast.success(t("cv.generated"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/cv/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "cv-learnspherehub.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download error"); }
  };

  const buildCvFromForm = () => ({
    name: form.name, title: form.title, summary: form.summary,
    contact: { email: form.email, phone: form.phone, location: form.location, links: "" },
    experience: form.experience, education: form.education,
    skills: (form.skills || "").split(",").map(s => s.trim()).filter(Boolean),
    languages: (form.languages || "").split(",").map(s => s.trim()).filter(Boolean),
    certifications: (form.certifications || "").split(",").map(s => s.trim()).filter(Boolean),
  });

  const updateCv = async () => {
    setLoading(true); setResult(null);
    try {
      const cvData = buildCvFromForm();
      const { data } = await api.post(`/cv/${editingId}/update`, { cv: cvData, template, language, photo_path: photoPath }, { timeout: 120000 });
      setResult({ id: editingId, cv: cvData, pdf_ready: data.pdf_ready, paid: data.paid });
      loadPreview(editingId);
      loadHistory();
      toast.success(t("cv.updated"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const loadForEdit = async (id) => {
    try {
      const { data } = await api.get(`/cv/${id}`);
      const c = data.cv || {};
      setEditingId(id);
      setTemplate(data.template || "moderno");
      setLanguage(data.language || "it");
      setPhotoPath(data.photo_path || null);
      setForm({
        name: c.name || "", title: c.title || "", email: c.contact?.email || "", phone: c.contact?.phone || "",
        location: c.contact?.location || "", summary: c.summary || "",
        skills: (c.skills || []).join(", "), languages: (c.languages || []).join(", "),
        certifications: (c.certifications || []).join(", "),
        experience: c.experience?.length ? c.experience : [{ ...emptyExp }],
        education: c.education?.length ? c.education : [{ ...emptyEdu }],
      });
      setResult({ id, cv: c, pdf_ready: data.pdf_ready, paid: data.paid });
      loadPreview(id);
      setStep("form");
    } catch { toast.error(t("cv.notFound")); }
  };

  const exitEdit = () => { setEditingId(null); setResult(null); setStep("gallery"); };

  const setExp = (i, k, v) => setForm(f => ({ ...f, experience: f.experience.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const setEdu = (i, k, v) => setForm(f => ({ ...f, education: f.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));

  const cv = result?.cv;
  const selTpl = templates.find(x => x.id === template);

  return (
    <div data-testid="cv-smart-page">
      <section className="relative bg-brand-navy text-white overflow-hidden group">
        <AdminEditableImage as="background" imageKey="cv_premium_hero" defaultSrc="/smart-services-backgrounds/smart-service-cv.webp"
          selfGroup={false} className="absolute inset-0" style={{ backgroundColor: "#050d1f" }} />
        <div className="absolute inset-0" aria-hidden="true" style={{ background: "linear-gradient(90deg, rgba(5,13,31,0.62) 0%, rgba(5,13,31,0.28) 44%, rgba(5,13,31,0.06) 72%, rgba(5,13,31,0) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-16">
          <Link to="/servizi-smart" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> {t("nav.smart")}</Link>
          <div className="mt-4 flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-orange/20 text-brand-orange px-3 py-1 text-xs font-bold uppercase tracking-widest"><Sparkles className="h-3.5 w-3.5" /> Premium</span>
          </div>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold font-heading tracking-tight">CV Smart Premium</h1>
          <p className="mt-3 text-white/70 max-w-2xl">{t("cv.subtitle")}</p>
        </div>
      </section>

      {isAdmin && (
        <div className="bg-brand-navy/5 border-b border-brand-navy/10" data-testid="cv-admin-preview-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-brand-navy/70">
              <Lock className="h-4 w-4 text-brand-orange" />
              <span>{previewCustomer ? t("cv.previewCustomerOn") : t("cv.previewCustomerOff")}</span>
            </div>
            <button
              data-testid="cv-preview-customer-toggle"
              onClick={() => setPreviewCustomer((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${previewCustomer ? "bg-brand-orange text-white hover:bg-brand-orange/90" : "bg-white border border-brand-navy/15 text-brand-navy hover:border-brand-electric"}`}>
              {previewCustomer ? <Check className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {t("cv.previewAsCustomer")}
            </button>
          </div>
        </div>
      )}

      {access === null ? (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 flex justify-center">
          <div className="h-10 w-10 rounded-full border-4 border-brand-electric/30 border-t-brand-electric animate-spin" />
        </div>
      ) : step === "gallery" ? (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="cv-gallery">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold font-heading text-brand-navy">{t("cv.chooseTitle")}</h2>
              <p className="mt-2 text-brand-navy/60 max-w-2xl">{t("cv.chooseSubtitle")}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Label className="text-brand-navy text-sm">{t("cv.language")}</Label>
              <div className="flex rounded-full border border-brand-navy/15 overflow-hidden text-sm">
                <button data-testid="cv-lang-it" onClick={() => setLanguage("it")} className={`px-4 py-1.5 ${language === "it" ? "bg-brand-electric text-white" : "text-brand-navy"}`}>IT</button>
                <button data-testid="cv-lang-en" onClick={() => setLanguage("en")} className={`px-4 py-1.5 ${language === "en" ? "bg-brand-electric text-white" : "text-brand-navy"}`}>EN</button>
              </div>
            </div>
          </div>

          {templates.length === 0 ? (
            <div className="py-20 flex justify-center"><div className="h-10 w-10 rounded-full border-4 border-brand-electric/30 border-t-brand-electric animate-spin" /></div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((tp) => (
                <div key={tp.id} data-testid={`cv-tpl-card-${tp.id}`}
                  className={`group flex flex-col rounded-2xl border-2 overflow-hidden bg-white transition-all ${template === tp.id ? "border-brand-electric ring-2 ring-brand-electric/20" : "border-brand-navy/10 hover:border-brand-navy/30 hover:shadow-lg"}`}>
                  <div className="relative bg-brand-light border-b border-brand-navy/5" style={{ aspectRatio: "210 / 297" }}>
                    {tp.preview_img || tp.preview_path
                      ? <img src={tp.preview_img || `${API}/media/${tp.preview_path}`} alt={tp.name} className="w-full h-full object-contain" />
                      : <div className="w-full h-full flex items-center justify-center text-brand-navy/20"><FileText className="h-10 w-10" /></div>}
                    {template === tp.id && (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-brand-electric text-white text-xs font-semibold px-2.5 py-1"><Check className="h-3.5 w-3.5" /> {t("cv.selected")}</span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg text-brand-navy">{tp.name}</h3>
                    <p className="mt-1.5 text-sm text-brand-navy/60 leading-relaxed">{tp.desc}</p>
                    <p className="mt-3 text-xs text-brand-navy/50"><span className="font-semibold text-brand-navy/70">{t("cv.idealFor")}:</span> {tp.sectors}</p>
                    <Button data-testid={`cv-select-${tp.id}`} onClick={() => { setTemplate(tp.id); setStep("form"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className={`mt-5 rounded-full h-11 gap-2 ${template === tp.id ? "bg-brand-navy hover:bg-brand-navy/90" : "bg-brand-electric hover:bg-brand-electric/90"} text-white`}>
                      <Check className="h-4 w-4" /> {t("cv.selectTemplate")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          {editingId && (
            <div data-testid="cv-edit-banner" className="mb-5 rounded-xl border border-brand-orange/30 bg-brand-orange/10 px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-brand-navy/80"><Pencil className="h-4 w-4 text-brand-orange" /> {t("cv.editingMode")}</div>
              <button data-testid="cv-exit-edit" onClick={exitEdit} className="text-sm font-medium text-brand-navy/60 hover:text-brand-navy underline">{t("cv.exitEdit")}</button>
            </div>
          )}
          <div className="mb-5 flex items-center gap-2">
            <Label className="text-brand-navy">{t("cv.language")}</Label>
            <div className="flex rounded-full border border-brand-navy/15 overflow-hidden text-sm">
              <button data-testid="cv-lang-it" onClick={() => setLanguage("it")} className={`px-4 py-1.5 ${language === "it" ? "bg-brand-electric text-white" : "text-brand-navy"}`}>IT</button>
              <button data-testid="cv-lang-en" onClick={() => setLanguage("en")} className={`px-4 py-1.5 ${language === "en" ? "bg-brand-electric text-white" : "text-brand-navy"}`}>EN</button>
            </div>
          </div>

          <div className="mb-6 rounded-xl border border-brand-navy/10 bg-brand-light/50 p-3 flex items-center gap-3" data-testid="cv-selected-template">
            <div className="h-20 w-14 rounded-md overflow-hidden border border-brand-navy/10 bg-white shrink-0">
              {(selTpl?.preview_img || selTpl?.preview_path) && <img src={selTpl.preview_img || `${API}/media/${selTpl.preview_path}`} alt="" className="w-full h-full object-contain" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-brand-navy/50">{t("cv.selectedTemplate")}</p>
              <p className="font-heading font-bold text-brand-navy truncate">{selTpl?.name || template}</p>
              <p className="text-xs text-brand-navy/50 truncate">{selTpl?.sectors}</p>
            </div>
            <Button data-testid="cv-change-template" variant="outline" size="sm" onClick={() => setStep("gallery")}
              className="rounded-full text-brand-navy border-brand-navy/20 hover:border-brand-electric shrink-0">{t("cv.changeTemplate")}</Button>
          </div>

          <div className="mb-6">
            <Label className="text-brand-navy">{t("cv.photo")}</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-16 w-16 rounded-full overflow-hidden bg-brand-light border border-brand-navy/10 flex items-center justify-center shrink-0">
                {photoPath ? <img src={`${API}/media/${photoPath}`} alt="" className="w-full h-full object-cover" /> : <FileText className="h-5 w-5 text-brand-navy/30" />}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                {photoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {photoPath ? t("cv.changePhoto") : t("cv.addPhoto")}
                <input data-testid="cv-photo-upload" type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
              {photoPath && <button data-testid="cv-photo-remove" onClick={() => setPhotoPath(null)} className="text-brand-navy/40 hover:text-red-500"><X className="h-4 w-4" /></button>}
              <span className="text-xs text-brand-navy/40">{t("cv.optional")}</span>
            </div>
          </div>

          <Tabs defaultValue="scratch">
            <TabsList className="bg-white border border-brand-navy/10 rounded-full p-1 flex-wrap h-auto">
              <TabsTrigger value="scratch" data-testid="cv-tab-scratch" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Plus className="h-4 w-4" /> {t("cv.fromScratch")}</TabsTrigger>
              <TabsTrigger value="improve" data-testid="cv-tab-improve" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Sparkles className="h-4 w-4" /> {t("cv.improve")}</TabsTrigger>
              <TabsTrigger value="tailor" data-testid="cv-tab-tailor" className="rounded-full data-[state=active]:bg-brand-electric data-[state=active]:text-white gap-2"><Upload className="h-4 w-4" /> {t("cv.tailor")}</TabsTrigger>
            </TabsList>

            <TabsContent value="scratch" className="mt-5 space-y-4">
              <p className="text-sm text-brand-navy/60">{t("cv.scratchDesc")}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("cv.name")}</Label><Input data-testid="cv-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cv.role")}</Label><Input data-testid="cv-role" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cv.phone")}</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>{t("cv.summary")}</Label><Textarea rows={2} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} className="mt-1.5" /></div>
              <div>
                <Label>{t("cv.experience")}</Label>
                {form.experience.map((ex, i) => (
                  <div key={i} className="mt-2 grid sm:grid-cols-2 gap-2 rounded-xl border border-brand-navy/10 p-3">
                    <Input placeholder={t("cv.role")} value={ex.role} onChange={e => setExp(i, "role", e.target.value)} />
                    <Input placeholder={t("cv.company")} value={ex.company} onChange={e => setExp(i, "company", e.target.value)} />
                    <Input placeholder={t("cv.period")} value={ex.period} onChange={e => setExp(i, "period", e.target.value)} />
                    <Input placeholder={t("cv.descr")} value={ex.description} onChange={e => setExp(i, "description", e.target.value)} />
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2 rounded-full text-brand-navy" onClick={() => setForm(f => ({ ...f, experience: [...f.experience, { ...emptyExp }] }))}><Plus className="h-4 w-4 mr-1" /> {t("cv.addExp")}</Button>
              </div>
              <div>
                <Label>{t("cv.education")}</Label>
                {form.education.map((ed, i) => (
                  <div key={i} className="mt-2 grid sm:grid-cols-3 gap-2 rounded-xl border border-brand-navy/10 p-3">
                    <Input placeholder={t("cv.degree")} value={ed.degree} onChange={e => setEdu(i, "degree", e.target.value)} />
                    <Input placeholder={t("cv.institution")} value={ed.institution} onChange={e => setEdu(i, "institution", e.target.value)} />
                    <Input placeholder={t("cv.period")} value={ed.period} onChange={e => setEdu(i, "period", e.target.value)} />
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2 rounded-full text-brand-navy" onClick={() => setForm(f => ({ ...f, education: [...f.education, { ...emptyEdu }] }))}><Plus className="h-4 w-4 mr-1" /> {t("cv.addEdu")}</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("cv.skills")}</Label><Input value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} className="mt-1.5" placeholder="Comma, separated" /></div>
                <div><Label>{t("cv.languages")}</Label><Input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} className="mt-1.5" placeholder="Comma, separated" /></div>
              </div>
              <div><Label>{t("cv.certifications")}</Label><Input data-testid="cv-certifications" value={form.certifications} onChange={e => setForm({ ...form, certifications: e.target.value })} className="mt-1.5" placeholder="Comma, separated" /></div>
              <div><Label>{t("cv.jobOptional")}</Label><Textarea rows={3} value={scratchJob} onChange={e => setScratchJob(e.target.value)} className="mt-1.5" placeholder={t("cv.jobPlaceholder")} /></div>
              {editingId ? (
                <Button data-testid="cv-update-btn" onClick={updateCv} disabled={loading} className="rounded-full h-11 px-6 bg-brand-orange hover:bg-brand-orange/90 text-white gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} {t("cv.updateRegenerate")}
                </Button>
              ) : (
                <Button data-testid="cv-generate-scratch" onClick={() => generate("scratch")} disabled={loading} className="rounded-full h-11 px-6 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("cv.generate")}
                </Button>
              )}
            </TabsContent>

            <TabsContent value="improve" className="mt-5 space-y-4">
              <p className="text-sm text-brand-navy/60">{t("cv.improveDesc")}</p>
              <div>
                <Label>{t("cv.uploadOld")}</Label>
                <div className="mt-1.5">
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                    {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {parsing ? t("common.loading") : t("cv.chooseFile")}
                    <input data-testid="cv-improve-upload" type="file" accept=".pdf,.docx,image/*" className="hidden" onChange={onUpload} />
                  </label>
                  <span className="ml-3 text-xs text-brand-navy/50">PDF · DOCX · {t("cv.image")}</span>
                </div>
              </div>
              <div><Label>{t("cv.cvText")}</Label><Textarea data-testid="cv-improve-text" rows={6} value={cvText} onChange={e => setCvText(e.target.value)} className="mt-1.5" placeholder={t("cv.cvTextPlaceholder")} /></div>
              <div><Label>{t("cv.jobOptional")}</Label><Textarea data-testid="cv-improve-job" rows={3} value={jobText} onChange={e => setJobText(e.target.value)} className="mt-1.5" placeholder={t("cv.jobPlaceholder")} /></div>
              <Button data-testid="cv-generate-improve" onClick={() => generate("improve")} disabled={loading || !cvText} className="rounded-full h-11 px-6 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("cv.generate")}
              </Button>
            </TabsContent>

            <TabsContent value="tailor" className="mt-5 space-y-4">
              <p className="text-sm text-brand-navy/60">{t("cv.tailorDesc")}</p>
              <div>
                <Label>{t("cv.uploadOld")}</Label>
                <div className="mt-1.5">
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-brand-navy/20 px-4 py-2 text-sm text-brand-navy hover:border-brand-electric">
                    {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {parsing ? t("common.loading") : t("cv.chooseFile")}
                    <input data-testid="cv-upload" type="file" accept=".pdf,.docx,image/*" className="hidden" onChange={onUpload} />
                  </label>
                  <span className="ml-3 text-xs text-brand-navy/50">PDF · DOCX · {t("cv.image")}</span>
                </div>
              </div>
              <div><Label>{t("cv.cvText")}</Label><Textarea data-testid="cv-text" rows={6} value={cvText} onChange={e => setCvText(e.target.value)} className="mt-1.5" placeholder={t("cv.cvTextPlaceholder")} /></div>
              <div><Label>{t("cv.jobAd")}</Label><Textarea data-testid="cv-job" rows={5} value={jobText} onChange={e => setJobText(e.target.value)} className="mt-1.5" placeholder={t("cv.jobPlaceholder")} /></div>
              <Button data-testid="cv-generate-tailor" onClick={() => generate("tailor")} disabled={loading || (!cvText && !jobText)} className="rounded-full h-11 px-6 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("cv.generate")}
              </Button>
            </TabsContent>
          </Tabs>
        </div>

        {/* Result / history */}
        <div className="lg:col-span-2 space-y-6">
          {loading && (
            <div className="rounded-2xl border border-brand-navy/10 bg-white p-8 text-center">
              <Loader2 className="h-8 w-8 mx-auto text-brand-electric animate-spin" />
              <p className="mt-3 text-brand-navy/60 text-sm">{t("cv.working")}</p>
            </div>
          )}
          {result && !loading && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="cv-result" className="rounded-2xl border border-brand-navy/10 bg-white p-5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-bold text-brand-navy">{t("cv.preview")}</h3>
                  {canDl(result.paid)
                    ? <span data-testid="cv-paid-badge" className="text-[11px] font-bold text-green-700 bg-green-50 rounded-full px-2 py-0.5">{t("cv.paidBadge")}</span>
                    : <span data-testid="cv-watermark-badge" className="text-[11px] font-bold text-brand-orange bg-brand-orange/10 rounded-full px-2 py-0.5">{t("cv.watermarkBadge")}</span>}
                </div>
                {result.pdf_ready && (
                  <div className="flex items-center gap-2">
                    <Button data-testid="cv-download" onClick={() => canDl(result.paid) ? download(result.id) : openPaywall(result.id)} size="sm" className="rounded-full bg-brand-orange hover:bg-brand-orange/90 text-white gap-1.5">{canDl(result.paid) ? <Download className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />} PDF</Button>
                  </div>
                )}
              </div>
              {previewUrl ? (
                <div className="mt-4 rounded-lg overflow-hidden border border-brand-navy/10 bg-brand-light">
                  <img data-testid="cv-preview-image" src={previewUrl} alt="CV preview" className="w-full h-auto object-contain" />
                </div>
              ) : (
                <div className="mt-4 py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-electric" /></div>
              )}
              {!canDl(result.paid) && (
                <button data-testid="cv-unlock-cta" onClick={() => openPaywall(result.id)} className="mt-4 w-full rounded-full h-11 bg-brand-electric hover:bg-brand-electric/90 text-white font-semibold inline-flex items-center justify-center gap-2"><Sparkles className="h-4 w-4" /> {t("cv.unlockCta")}</button>
              )}
            </motion.div>
          )}
          <div className="rounded-2xl border border-brand-navy/10 bg-white p-6">
            <h3 className="font-heading font-bold text-brand-navy">{t("cv.history")}</h3>
            {history.length === 0 ? <p className="mt-3 text-sm text-brand-navy/50">{t("cv.noHistory")}</p> : (
              <div className="mt-3 space-y-2">
                {history.map(h => (
                  <div key={h.id} data-testid={`cv-hist-${h.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-brand-navy/10 px-3 py-2">
                    {renameId === h.id ? (
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <Input data-testid={`cv-rename-input-${h.id}`} value={renameVal} onChange={e => setRenameVal(e.target.value)} className="h-8 text-sm" />
                        <button data-testid={`cv-rename-save-${h.id}`} onClick={() => doRename(h.id)} className="text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setRenameId(null)} className="text-brand-navy/40"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <>
                        <div className="min-w-0"><p className="text-sm font-medium text-brand-navy truncate">{h.title}</p><p className="text-[11px] text-brand-navy/40">{h.language?.toUpperCase()} · {h.mode} · {h.template}</p></div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button data-testid={`cv-edit-${h.id}`} title={t("cv.edit")} onClick={() => { loadForEdit(h.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-brand-navy/40 hover:text-brand-orange"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`cv-rename-${h.id}`} title={t("cv.rename")} onClick={() => { setRenameId(h.id); setRenameVal(h.title); }} className="text-brand-navy/40 hover:text-brand-electric"><FileText className="h-4 w-4" /></button>
                          {h.pdf_ready && <button data-testid={`cv-hist-download-${h.id}`} title="PDF" onClick={() => canDl(h.paid) ? download(h.id) : openPaywall(h.id)} className="text-brand-electric hover:text-brand-orange">{canDl(h.paid) ? <Download className="h-4 w-4" /> : <Lock className="h-4 w-4" />}</button>}
                          <button data-testid={`cv-delete-${h.id}`} title={t("cv.deleteAction")} onClick={() => doDelete(h.id)} className="text-brand-navy/40 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
        <DialogContent data-testid="cv-paywall-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">{t("cv.paywallTitle")}</DialogTitle>
            <DialogDescription className="text-brand-navy/70 text-sm">{t("cv.paywallLead")}</DialogDescription>
          </DialogHeader>
          <div data-testid="cv-paywall-include" className="mt-3 rounded-lg bg-brand-electric/5 border border-brand-electric/15 px-3 py-2.5 text-sm font-medium text-brand-navy">
            {t("cv.paywallInclude")}
          </div>
          <ul className="mt-3 space-y-1.5 text-sm text-brand-navy/80">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> {t("cv.paywallItem1")}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> {t("cv.paywallItem2")}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> {t("cv.paywallItem3")}</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> {t("cv.paywallItem4")}</li>
          </ul>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-heading font-extrabold text-3xl text-brand-navy">{access?.symbol || "€"}{Number(access?.price ?? 2.99).toFixed(2).replace(".", ",")}</span>
            <span className="text-brand-navy/50 text-sm">{t("cv.perCv")}</span>
          </div>
          <p data-testid="cv-paywall-editnote" className="mt-3 text-xs leading-relaxed text-brand-navy/60 border-l-2 border-brand-orange/40 pl-3">
            {t("cv.paywallEditNote")}
          </p>
          <CheckoutConsents variant="service" terms={pwTerms} setTerms={setPwTerms} immediate={pwImmediate} setImmediate={setPwImmediate} idPrefix="cv-paywall" />
          <Button data-testid="cv-paywall-checkout" onClick={startCheckout} disabled={checkingOut || !pwTerms || !pwImmediate}
            className="mt-4 w-full rounded-full h-12 bg-brand-electric hover:bg-brand-electric/90 text-white gap-2">
            {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t("cv.unlockMyCv")}
          </Button>
          <p className="mt-2 text-center text-xs text-brand-navy/40">{t("cv.stripeTest")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CvSmartPremium;
